import React from "react";

/**
 * SummaryCards renders 4 KPI cards for the Access Manager panel:
 * - Total Users
 * - Available Scopes
 * - Scope Counts (summary of most-granted scopes)
 * - Enforcement Enabled status
 *
 * Shows skeleton placeholders while loading and inline error with retry on failure.
 */
export default function SummaryCards({ summary, loading, error, onRetry }) {
  if (error) {
    return (
      <div className="summary-cards summary-cards--error" role="alert">
        <p className="summary-cards__error-message">{error}</p>
        <button
          className="summary-cards__retry-btn"
          onClick={onRetry}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="summary-cards" aria-busy="true" aria-label="Loading summary data">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="summary-cards__card summary-cards__card--skeleton">
            <div className="summary-cards__skeleton-title" />
            <div className="summary-cards__skeleton-value" />
          </div>
        ))}
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const scopeCountsLabel = formatScopeCounts(summary.scope_counts);

  return (
    <div className="summary-cards">
      <div className="summary-cards__card">
        <span className="summary-cards__label">Total Users</span>
        <span className="summary-cards__value">{summary.total_users}</span>
      </div>
      <div className="summary-cards__card">
        <span className="summary-cards__label">Available Scopes</span>
        <span className="summary-cards__value">{summary.available_scopes}</span>
      </div>
      <div className="summary-cards__card">
        <span className="summary-cards__label">Scope Counts</span>
        <span className="summary-cards__value summary-cards__value--small">
          {scopeCountsLabel}
        </span>
      </div>
      <div className="summary-cards__card">
        <span className="summary-cards__label">Enforcement</span>
        <span className="summary-cards__value">
          {summary.enforcement_enabled ? "Enabled" : "Disabled"}
        </span>
      </div>
    </div>
  );
}

/**
 * Formats scope_counts object into a concise display string.
 * Shows top 3 scopes by count, e.g. "admin: 5, supervisor: 3, semantic: 2"
 */
function formatScopeCounts(scopeCounts) {
  if (!scopeCounts || typeof scopeCounts !== "object") {
    return "—";
  }

  const entries = Object.entries(scopeCounts);
  if (entries.length === 0) {
    return "—";
  }

  const sorted = entries.sort((a, b) => b[1] - a[1]);
  const top = sorted.slice(0, 3);
  return top.map(([scope, count]) => `${scope}: ${count}`).join(", ");
}
